import React, { useState, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { postApi } from "../../../../api/getUser";
import { getDeclineErrorMessage } from "../../../../shared/helpers";
import { formatNumber } from "../../../../shared/helpers";
import { LinkSpan } from "../../../../shared/components/form/FormElements";
import format from "date-fns/format";
import {
  ActionIconContainer,
  AddFormButton,
  FilterButton,
  RedButton,
} from "../../../../shared/components/TableElements";
import ConfirmAddonModal from "../../components/ConfirmAddonModal";
import { NumberInput } from "../../NewPlan";
import { useStripe } from "@stripe/react-stripe-js";

const ESSENTIAL = "5";
const ADVANCED = "6";

const ChangePlanModal = ({
  show,
  onHide,
  subscriptionData,
  priceDetailsData,
  subPaymentData,
  //telematicsData,
  onSuccess,
  onUpdateSuccess,
  onCancelSuccess,
  paymentData,
  telematicsStatus,
  wexStatus,
  equipmentStatus,
  telematicsUnitPrice,
  wexUnitPrice,
  equipmentUnitPrice,
  equipmentQuantity,
  telematicsItemId,
  fuelCardItemId,
  equipmentSubscriptionId,
  isLegacyPricing,
  hasActiveTelematicsConnection,
  onDeleteTelematicsConnections,
}) => {
  const { t } = useTranslation("common");

  const stripe = useStripe();
  const currentProduct = subscriptionData?.product;
  const isCurrentMonthly = Number(subscriptionData?.duration) === 1;
  const initialVehicleCount =
    subPaymentData?.quantity || subscriptionData?.no_of_vehicles || 5;

  const [selectedProduct, setSelectedProduct] = useState(currentProduct);
  const [selectedDuration, setSelectedDuration] = useState(
    isCurrentMonthly ? "monthly" : "yearly",
  );
  const currentPeriodLabel = isCurrentMonthly ? t("month_sm") : t("year_sm");

  const [vehicleCount, setVehicleCount] = useState(initialVehicleCount);
  const [view, setView] = useState("main"); // "main" | "cancel" | "cancel_reason" | "confirm"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelComment, setCancelComment] = useState("");

  const CANCEL_REASONS = [
    { label: t("cancel_reason_too_expensive"), value: "too_expensive" },
    { label: t("cancel_reason_missing_features"), value: "missing_features" },
    { label: t("cancel_reason_switched_service"), value: "switched_service" },
    { label: t("cancel_reason_unused"), value: "unused" },
    { label: t("cancel_reason_customer_service"), value: "customer_service" },
    { label: t("cancel_reason_too_complex"), value: "too_complex" },
    { label: t("cancel_reason_low_quality"), value: "low_quality" },
    { label: t("cancel_reason_other"), value: "other" },
  ];

  const initialDuration = isCurrentMonthly ? "monthly" : "yearly";
  const hasChanges =
    selectedProduct !== currentProduct ||
    selectedDuration !== initialDuration ||
    Number(vehicleCount) !== Number(initialVehicleCount);

  const isMonthlySelected = selectedDuration === "monthly";
  const periodLabel = isMonthlySelected ? t("month_sm") : t("year_sm");
  const currency = priceDetailsData?.currency_code || "";

  const unitPrice = useMemo(() => {
    if (!priceDetailsData) return 0;
    if (selectedProduct === ESSENTIAL) {
      return isMonthlySelected
        ? isLegacyPricing
          ? priceDetailsData.essential_monthly_price_legacy
          : priceDetailsData.essential_monthly_price
        : isLegacyPricing
          ? priceDetailsData.essential_yearly_price_legacy
          : priceDetailsData.essential_yearly_price;
    }
    return isMonthlySelected
      ? isLegacyPricing
        ? priceDetailsData.advance_monthly_price_legacy
        : priceDetailsData.advance_monthly_price
      : isLegacyPricing
        ? priceDetailsData.advance_yearly_price_legacy
        : priceDetailsData.advance_yearly_price;
  }, [selectedProduct, isMonthlySelected, priceDetailsData, isLegacyPricing]);

  const planTotal = Number(unitPrice) * Number(vehicleCount);

  const isNotOldAdvancedUser =
    !isLegacyPricing || (isLegacyPricing && telematicsItemId);
  const isLegacyAdvancedDowngrade =
    isLegacyPricing &&
    currentProduct === ADVANCED &&
    !telematicsItemId &&
    hasActiveTelematicsConnection;
  const telematicsIsActive =
    (telematicsStatus === "active" || !!telematicsItemId) &&
    isNotOldAdvancedUser;
  const wexIsActive = wexStatus === "active" || !!fuelCardItemId;
  const equipmentIsActive =
    equipmentStatus === "active" || !!equipmentSubscriptionId;

  const selectedTelematicsUnitPrice = isMonthlySelected
    ? priceDetailsData?.telematics_monthly_price || telematicsUnitPrice
    : priceDetailsData?.telematics_yearly_price || telematicsUnitPrice;

  const selectedWexUnitPrice = isMonthlySelected
    ? priceDetailsData?.fuel_card_monthly_price || wexUnitPrice
    : priceDetailsData?.fuel_card_yearly_price || wexUnitPrice;

  const selectedEquipmentUnitPrice = isMonthlySelected
    ? priceDetailsData?.equipment_monthly_price || equipmentUnitPrice
    : priceDetailsData?.equipment_yearly_price || equipmentUnitPrice;

  const telematicsTotal =
    Number(selectedTelematicsUnitPrice || 0) * Number(vehicleCount);
  const wexTotal = Number(selectedWexUnitPrice || 0) * Number(vehicleCount);
  const equipmentTotal =
    Number(selectedEquipmentUnitPrice || 0) * Number(equipmentQuantity || 0);

  // Current-period totals for the cancel warning (always based on current subscription pricing)
  const currentTelematicsTotal =
    Number(telematicsUnitPrice || 0) * Number(vehicleCount);
  const currentWexTotal = Number(wexUnitPrice || 0) * Number(vehicleCount);
  const currentEquipmentTotal =
    Number(equipmentUnitPrice || 0) * Number(equipmentQuantity || 0);
  const grandTotal =
    planTotal +
    (telematicsIsActive ? telematicsTotal : 0) +
    (wexIsActive ? wexTotal : 0) +
    (equipmentIsActive ? equipmentTotal : 0);

  const periodEndDate = (() => {
    const ts = subPaymentData?.current_period_end;
    if (!ts) return "";
    const date = new Date(ts * 1000);
    return isNaN(date.getTime()) ? "" : format(date, "d MMMM yyyy");
  })();

  const hasPaymentMethod = !!paymentData?.type;

  const handleHide = () => {
    setView("main");
    setError("");
    setCancelReason("");
    setCancelComment("");
    setSelectedProduct(currentProduct);
    setSelectedDuration(isCurrentMonthly ? "monthly" : "yearly");
    setVehicleCount(initialVehicleCount);
    onHide();
  };

  const handleConfirmChanges = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        {
          querystring: "update_subscription_plan",
          new_product_id: Number(selectedProduct),
          duration: isMonthlySelected ? 1 : 2,
          quantity: vehicleCount,
        },
        "subscription",
      );
      if (res.success) {
        if (isLegacyAdvancedDowngrade && selectedProduct === ESSENTIAL) {
          onDeleteTelematicsConnections?.();
        }
        //onSuccess();
        onUpdateSuccess(vehicleCount);
        onHide();
      } else {
        console.log("res", res);
        if (res?.requires_action && res?.client_secret) {
          console.log("requires_action", res.requires_action);
          const confirmResult = await stripe.confirmCardPayment(
            res.client_secret,
          );
          console.log("confirmResult", confirmResult);

          //success
          if (confirmResult.paymentIntent) {
            if (isLegacyAdvancedDowngrade && selectedProduct === ESSENTIAL) {
              onDeleteTelematicsConnections?.();
            }
            //onSuccess();
            onUpdateSuccess(vehicleCount);
            onHide();
            return;
          }

          //error
          else if (confirmResult.error) {
            setError(
              getDeclineErrorMessage(
                "payment_intent_authentication_failure",
                t,
                "error_updating_plan",
              ),
            );
            return;
          } else {
            //generic error
            setError(
              getDeclineErrorMessage(
                "payment_intent_authentication_failure",
                t,
                "error_updating_plan",
              ),
            );
            return;
          }
        }

        setError(
          getDeclineErrorMessage(res.decline_code, t, "error_updating_plan"),
        );
      }
    } catch {
      setError(t("error_updating_plan"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        {
          querystring: "cancel_subscription",
          cancel_reason: cancelReason,
          cancel_comment: cancelReason === "other" ? cancelComment : "",
        },
        "subscription",
      );
      if (res.success) {
        onCancelSuccess();
        onHide();
      } else {
        setError(t("error_cancelling_subscription"));
      }
    } catch {
      setError(t("error_cancelling_subscription"));
    } finally {
      setLoading(false);
    }
  };

  const planName =
    selectedProduct === ESSENTIAL ? t("essential") : t("advanced");

  // ── Shared form (main + cancel views) ──────────────────────────────────────
  const renderForm = () => (
    <>
      <PlanPickerRow>
        <PlanOption
          $selected={selectedProduct === ESSENTIAL}
          onClick={() => setSelectedProduct(ESSENTIAL)}
        >
          {currentProduct === ESSENTIAL && (
            <CurrentBadge>{t("current")}</CurrentBadge>
          )}
          <PlanOptionName>{t("essential")}</PlanOptionName>
          <PlanOptionPrice>
            {`${currency} ${formatNumber(
              isMonthlySelected
                ? isLegacyPricing
                  ? priceDetailsData?.essential_monthly_price_legacy
                  : priceDetailsData?.essential_monthly_price
                : isLegacyPricing
                  ? priceDetailsData?.essential_yearly_price_legacy
                  : priceDetailsData?.essential_yearly_price,
              2,
            )} / ${t("vehicle_sm")} / ${periodLabel}`}
          </PlanOptionPrice>
        </PlanOption>

        <PlanOption
          $selected={selectedProduct === ADVANCED}
          onClick={() => setSelectedProduct(ADVANCED)}
        >
          {currentProduct === ADVANCED && (
            <CurrentBadge>{t("current")}</CurrentBadge>
          )}
          <PlanOptionName>{t("advanced")}</PlanOptionName>
          <PlanOptionPrice>
            {`${currency} ${formatNumber(
              isMonthlySelected
                ? isLegacyPricing
                  ? priceDetailsData?.advance_monthly_price_legacy
                  : priceDetailsData?.advance_monthly_price
                : isLegacyPricing
                  ? priceDetailsData?.advance_yearly_price_legacy
                  : priceDetailsData?.advance_yearly_price,
              2,
            )} / ${t("vehicle_sm")} / ${periodLabel}`}
          </PlanOptionPrice>
        </PlanOption>
      </PlanPickerRow>

      <DurationToggle>
        <DurationOption
          $active={isMonthlySelected}
          onClick={() => setSelectedDuration("monthly")}
        >
          {t("monthly")}
        </DurationOption>
        <DurationOption
          $active={!isMonthlySelected}
          onClick={() => setSelectedDuration("yearly")}
        >
          {t("yearly")}
        </DurationOption>
      </DurationToggle>

      <FormRow>
        <FormLabel>{t("number_of_vehicles")}</FormLabel>
        <NumberInput value={vehicleCount} setValue={setVehicleCount} />
        {/* <VehicleCounter>
          <CountBtn
            type="button"
            onClick={() => setVehicleCount((v) => Math.max(1, v - 1))}
          >
            −
          </CountBtn>
          <CountDisplay>{vehicleCount}</CountDisplay>
          <CountBtn type="button" onClick={() => setVehicleCount((v) => v + 1)}>
            +
          </CountBtn>
        </VehicleCounter> */}
      </FormRow>

      <PlanCostRow>
        <div>
          <PlanCostLabel>{t("plan_cost")}</PlanCostLabel>
          <PlanCostDetail>{`${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`}</PlanCostDetail>
        </div>
        <PlanCostAmount>{`${currency} ${formatNumber(planTotal, 2)}/${periodLabel}`}</PlanCostAmount>
      </PlanCostRow>
    </>
  );

  const confirmLineItems = [
    {
      name: `${planName} - ${isMonthlySelected ? t("monthly") : t("yearly")}`,
      detail: `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`,
      amount: `${currency} ${formatNumber(planTotal, 2)}/${periodLabel}`,
    },
    ...(telematicsIsActive
      ? [
          {
            name: t("telematics"),
            detail: `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(selectedTelematicsUnitPrice || 0, 2)}`,
            amount: `${currency} ${formatNumber(telematicsTotal, 2)}/${periodLabel}`,
          },
        ]
      : []),
    ...(wexIsActive
      ? [
          {
            name: t("wex_fuel_card"),
            detail: `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(selectedWexUnitPrice || 0, 2)}`,
            amount: `${currency} ${formatNumber(wexTotal, 2)}/${periodLabel}`,
          },
        ]
      : []),
    ...(equipmentIsActive
      ? [
          {
            name: t("small_equipment"),
            detail: `${equipmentQuantity} ${t("equipment_sm")} × ${currency} ${formatNumber(selectedEquipmentUnitPrice || 0, 2)}`,
            amount: `${currency} ${formatNumber(equipmentTotal, 2)}/${periodLabel}`,
          },
        ]
      : []),
  ];

  return (
    <>
      <Modal
        show={show && view !== "confirm" && view !== "cancel_reason"}
        onHide={handleHide}
        centered
        size="md"
      >
        <Modal.Header>
          <h4>{t("change_plan")}</h4>
          <div className="d-flex justify-content-end">
            <ActionIconContainer onClick={handleHide}>
              <i className="fi fi-rr-cross-small" />
            </ActionIconContainer>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* ── Main view ── */}
          {view === "main" && (
            <>
              {renderForm()}
              {isLegacyAdvancedDowngrade && selectedProduct === ESSENTIAL && (
                <TelematicsDowngradeWarning>
                  <i
                    className="fi fi-rr-triangle-warning"
                    style={{ position: "relative", top: 2, flexShrink: 0 }}
                  />
                  <div>{t("legacy_advanced_telematics_downgrade_warning")}</div>
                </TelematicsDowngradeWarning>
              )}
              {error && <ErrorText>{error}</ErrorText>}
              <div style={{ marginTop: 16 }}>
                <LinkSpan
                  style={{ fontSize: 13, color: "#9ca3af" }}
                  onClick={() => {
                    setView("cancel");
                    setError("");
                  }}
                >
                  {t("cancel_my_subscription")}
                </LinkSpan>
              </div>
            </>
          )}

          {/* ── Cancel view ── */}
          {view === "cancel" && (
            <>
              {renderForm()}
              <CancelWarningBox>
                <i
                  className="fi fi-rr-triangle-warning"
                  style={{ position: "relative", top: 2, flexShrink: 0 }}
                />
                <div>
                  {t("cancel_subscription_warning", { date: periodEndDate })}
                  {(telematicsIsActive || wexIsActive || equipmentIsActive) && (
                    <AddonList>
                      <AddonListTitle>
                        {t("active_addons_also_cancelled")}
                      </AddonListTitle>
                      {telematicsIsActive && (
                        <div>
                          {`• ${t("telematics")} (${currency} ${formatNumber(currentTelematicsTotal, 2)}/${currentPeriodLabel})`}
                        </div>
                      )}
                      {wexIsActive && (
                        <div>
                          {`• ${t("wex_fuel_card")} (${currency} ${formatNumber(currentWexTotal, 2)}/${currentPeriodLabel})`}
                        </div>
                      )}
                      {equipmentIsActive && (
                        <div>
                          {`• ${t("small_equipment")} (${currency} ${formatNumber(currentEquipmentTotal, 2)}/${currentPeriodLabel})`}
                        </div>
                      )}
                    </AddonList>
                  )}
                </div>
              </CancelWarningBox>
              {error && <ErrorText>{error}</ErrorText>}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex gap-2">
          {view === "main" && (
            <>
              <FilterButton onClick={handleHide} disabled={loading}>
                {t("cancel")}
              </FilterButton>
              <AddFormButton
                onClick={() => {
                  setError("");
                  setView("confirm");
                }}
                disabled={
                  loading ||
                  !hasChanges ||
                  (selectedProduct !== ESSENTIAL &&
                    selectedProduct !== ADVANCED)
                }
              >
                {t("review_changes")}
              </AddFormButton>
            </>
          )}

          {view === "cancel" && (
            <>
              <FilterButton
                onClick={() => {
                  setView("main");
                  setError("");
                }}
                disabled={loading}
              >
                {t("keep_my_plan")}
              </FilterButton>
              <AddFormButton
                onClick={() => {
                  setError("");
                  setView("cancel_reason");
                }}
                disabled={loading}
              >
                {t("continue")}
              </AddFormButton>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={show && view === "cancel_reason"}
        onHide={handleHide}
        centered
        size="md"
      >
        <Modal.Header>
          <h4>{t("cancel_subscription")}</h4>
          <div className="d-flex justify-content-end">
            <ActionIconContainer onClick={handleHide}>
              <i className="fi fi-rr-cross-small" />
            </ActionIconContainer>
          </div>
        </Modal.Header>

        <Modal.Body>
          <ReasonPrompt>{t("cancel_reason_prompt")}</ReasonPrompt>
          <ReasonList>
            {CANCEL_REASONS.map((reason) => (
              <ReasonItem key={reason.value}>
                <ReasonRadio
                  type="radio"
                  id={`reason-${reason.value}`}
                  name="cancel_reason"
                  value={reason.value}
                  checked={cancelReason === reason.value}
                  onChange={() => {
                    setCancelReason(reason.value);
                    setCancelComment("");
                  }}
                />
                <ReasonLabel htmlFor={`reason-${reason.value}`}>
                  {reason.label}
                </ReasonLabel>
              </ReasonItem>
            ))}
          </ReasonList>
          {cancelReason === "other" && (
            <ReasonTextarea
              placeholder={t("cancel_reason_other_placeholder")}
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
            />
          )}
          {error && <ErrorText>{error}</ErrorText>}
        </Modal.Body>

        <Modal.Footer className="d-flex gap-2">
          <FilterButton onClick={handleHide} disabled={loading}>
            {t("keep_my_plan")}
          </FilterButton>
          <RedButton
            onClick={handleCancelSubscription}
            disabled={loading || !cancelReason}
          >
            {loading ? t("cancelling") : t("cancel_subscription")}
          </RedButton>
        </Modal.Footer>
      </Modal>

      <ConfirmAddonModal
        show={show && view === "confirm"}
        onHide={() => {
          setView("main");
          setError("");
        }}
        lineItems={confirmLineItems}
        grandTotalLabel={
          isMonthlySelected ? t("new_monthly_total") : t("new_yearly_total")
        }
        grandTotalAmount={`${currency} ${formatNumber(grandTotal, 2)}/${periodLabel}`}
        billingNote={
          hasPaymentMethod ? t("confirm_changes_billing_note") : undefined
        }
        paymentData={paymentData}
        error={error}
        onConfirm={handleConfirmChanges}
        loading={loading}
        previewParams={{
          new_product_id: Number(selectedProduct),
          duration: isMonthlySelected ? 1 : 2,
          quantity: vehicleCount,
          equipment_quantity: equipmentQuantity,
          add_telematics: false,
          add_fuel_card: false,
        }}
      />
    </>
  );
};

export default ChangePlanModal;

// ── Styled components ─────────────────────────────────────────────────────────

const PlanPickerRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
`;

const PlanOption = styled.div`
  flex: 1;
  border: 2px solid ${(p) => (p.$selected ? "#d4a843" : "#e5e7eb")};
  border-radius: 8px;
  padding: 14px;
  cursor: pointer;
  position: relative;
  background: ${(p) => (p.$selected ? "#fffbeb" : "#ffffff")};
  transition: border-color 0.15s;
  &:hover {
    border-color: ${(p) => (p.$selected ? "#d4a843" : "#d1d5db")};
  }
`;

const CurrentBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 10px;
  font-weight: 600;
  background: #dcfce7;
  color: #166534;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
`;

const PlanOptionName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const PlanOptionPrice = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const DurationToggle = styled.div`
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const DurationOption = styled.button`
  flex: 1;
  padding: 8px 0;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: ${(p) => (p.$active ? "#1f2937" : "#ffffff")};
  color: ${(p) => (p.$active ? "#ffffff" : "black")};
  transition:
    background 0.15s,
    color 0.15s;
`;

const FormRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  //background: #f9fafb;
  //border: 1px solid #e5e7eb;
  //border-radius: 6px;
  padding: 10px 0px;
  margin-bottom: 10px;
`;

const FormLabel = styled.span`
  font-size: 13px;
  //color: #6b7280;
`;

const VehicleCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`;

const CountBtn = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  //background: #f3f4f6;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  color: #374151;
  font-family: inherit;
  &:hover {
    background: #e5e7eb;
  }
`;

const CountDisplay = styled.span`
  min-width: 36px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  padding: 0 6px;
`;

const PlanCostRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 10px;
`;

const PlanCostLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const PlanCostDetail = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
`;

const PlanCostAmount = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`;

const CancelWarningBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 14px 16px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;
  color: #991b1b;
  line-height: 1.55;
  margin-top: 14px;
`;

const AddonList = styled.div`
  margin-top: 8px;
  font-size: 12.5px;
`;

const AddonListTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #ef4444;
  margin-top: 8px;
`;

const ReasonPrompt = styled.p`
  font-size: 14px;
  margin-bottom: 16px;
`;

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReasonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReasonRadio = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
`;

const ReasonLabel = styled.label`
  font-size: 14px;
  cursor: pointer;
  margin: 0;
`;

const ReasonTextarea = styled.textarea`
  width: 100%;
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  min-height: 72px;
  outline: none;
  &:focus {
    border-color: #9ca3af;
  }
`;

const TelematicsDowngradeWarning = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
  margin-top: 10px;
`;
